import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { AdPod, AdPodStatus } from 'common/api/v1/types'
import { Api, AppDispatch, GlobalState } from '../../../store'
import Circle from '@mui/icons-material/FiberManualRecord'
import { useCallback, useEffect } from 'react'
import { Autocomplete, GridItem } from '../../common/Form'
import { useFormContext } from 'react-hook-form'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getAdPod } from '../../../redux/actions/adPodActions'
import Skeleton from '@mui/material/Skeleton'

const AdPodStatusIndicator = ({ adPod }: { adPod: AdPod }) => {
  const circle = () => {
    switch (adPod.status) {
      case AdPodStatus.pending:
        return <Circle color="warning" />
      case AdPodStatus.unhealthy:
        return <Circle color="error" />
      default:
        return <Circle color="success" />
    }
  }
  return (
    <Box display="flex" alignItems="center">
      {circle()}
    </Box>
  )
}

const adPodWarning = (status: AdPodStatus): string | undefined => {
  switch (status) {
    case AdPodStatus.pending:
      return 'Pending: Waiting for creatives to be uploaded'
    case AdPodStatus.unhealthy:
      return 'Unhealthy: One or more creatives are unhealthy'
    default:
      return undefined
  }
}

interface Props {
  formName: string
  adPodId?: string
  newLine?: boolean
  required?: boolean
}

export const AdPodSelector = ({ formName, adPodId, newLine, required }: Props) => {
  const adPodsApi = useCallback(Api.adPodsApi.listAdPods.bind(Api.adPodsApi), [])
  const dispatch = useDispatch<AppDispatch>()

  const { getValues, setValue } = useFormContext()

  const selectedAdPod = useSelector(({ adPodReducer }: GlobalState) => adPodReducer.selectedAdPod, shallowEqual)

  useEffect(() => {
    if (adPodId) {
      dispatch(getAdPod(adPodId))
    }
  }, [adPodId, dispatch])

  if (adPodId && !selectedAdPod) {
    return (
      <GridItem xs={4} newLine={newLine}>
        <Skeleton
          variant="rounded"
          sx={{
            width: '100%',
            height: '56px',
            marginTop: 2,
          }}
          animation="wave"
        />
      </GridItem>
    )
  }

  return (
    <Autocomplete<AdPod>
      name={formName}
      label="Ad Pod"
      api={adPodsApi}
      newLine={newLine}
      required={required}
      defaultOption={selectedAdPod}
      getOptionValue={(adPod) => adPod.id}
      getOptionLabel={(adPod) => adPod.name}
      optionComparator={(o1: AdPod, o2: AdPod) => o1.id === o2.id}
      disabled={getValues('adInsertionEnabled') === false}
      xs={4}
      onClear={() => {
        setValue(formName, null)
      }}
      renderOption={({ key, ...props }, adPod) => {
        const durationMs = adPod?.creatives.reduce((acc, creative) => {
          if (creative.userDurationMs) {
            return acc + creative.userDurationMs
          }

          if (creative.metadata?.format?.durationMs) {
            return acc + creative.metadata.format.durationMs
          }

          return acc
        }, 0)

        return (
          <li key={key} {...props}>
            <Box display="flex" alignItems="center">
              {adPod.status && <AdPodStatusIndicator adPod={adPod} />}
              <Box display="block" sx={{ ml: 1 }}>
                <Typography component="div" variant="body2" color="textPrimary">
                  <strong>{adPod.name}</strong> {durationMs ? ` - ${Math.round(durationMs / 1000)} sec` : ''}
                </Typography>
                <Typography component="div" variant="body2" color="warning.light">
                  {adPodWarning(adPod.status)}
                </Typography>
              </Box>
            </Box>
          </li>
        )
      }}
    />
  )
}
